import { readCustomerId } from "@/services/api-service/customer-skus/customer-skus.types";

export type ReorderSkuStep = "confirmRelated" | "selectRelated" | "review";

export interface IReorderSkuItem {
  id: string;
  sku: string;
  quantity: number;
  boardMissionId?: string;
  customerId?: string;
  orderItemId?: string;
  productId?: string;
  unitPrice?: number | string;
  skuGroupName?: string;
  isParent?: boolean;
  isSkuGroup?: boolean;
}

/** One group from GetDefaultCustomerSKUsByCustomerId. Price stays on the group. */
export interface IReorderSkuGroup {
  id: string;
  skuGroupName: string;
  price: number;
  orderItemId: string;
  skus: IReorderSkuItem[];
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" ? (value as Record<string, unknown>) : null;

export const mapToReorderSkuItem = (
  raw: unknown,
  isParent = false
): IReorderSkuItem | null => {
  if (typeof raw === "string") {
    const sku = raw.trim();
    if (!sku) {
      return null;
    }
    return {
      id: sku,
      sku,
      quantity: 1,
      isParent,
    };
  }

  const item = asRecord(raw);
  if (!item) {
    return null;
  }

  const sku = String(
    item.sku || item.SKU || item.value || item.name || item.text || ""
  ).trim();
  const id = String(item.id || item.ID || item.customerSkuId || sku);
  if (!id && !sku) {
    return null;
  }

  const quantityValue = Number(item.quantity ?? item.Quantity ?? 1);

  return {
    id: id || sku,
    sku,
    quantity: Number.isFinite(quantityValue) && quantityValue > 0 ? quantityValue : 1,
    boardMissionId: String(
      item.boardMissionId || item.BoardMissionId || ""
    ),
    customerId: readCustomerId(item),
    orderItemId: String(item.orderItemId || item.OrderItemId || ""),
    productId: String(item.productId || item.ProductId || ""),
    unitPrice: (item.unitPrice ?? item.UnitPrice ?? item.price) as
      | number
      | string
      | undefined,
    skuGroupName: String(
      item.skuGroupName ||
      item.SkuGroupName ||
      item.SKUGroupName ||
      item.code ||
      item.Code ||
      ""
    ),
    isParent,
    isSkuGroup: !!(item.isSkuGroup ?? item.IsSkuGroup),
  };
};

export const isSameReorderSku = (
  left?: IReorderSkuItem | null,
  right?: IReorderSkuItem | null
) => {
  if (!left || !right) {
    return false;
  }
  if (left.id && right.id && left.id === right.id) {
    return true;
  }
  const leftName = left.sku.trim().toLowerCase();
  const rightName = right.sku.trim().toLowerCase();
  return !!leftName && leftName === rightName;
};

const groupPrice = (value: unknown) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return numeric;
    }
  }
  return 0;
};

/** Selection key for a SKU inside a group. Not sent to the reorder API. */
export const reorderSkuSelectionId = (
  orderItemId: string,
  index: number,
  sku: string
) => `${orderItemId}::${index}::${sku}`;

/**
 * Maps GetDefaultCustomerSKUsByCustomerId rows.
 * `skUs` is the response field; each string stays tied to its group's orderItemId.
 */
export const mapDefaultCustomerSkuGroups = (
  rows: unknown[]
): IReorderSkuGroup[] =>
  rows.flatMap((raw, groupIndex) => {
    const item = asRecord(raw);
    if (!item) {
      return [];
    }

    const orderItemId = String(item.orderItemId || item.OrderItemId || "").trim();
    const skuGroupName = String(
      item.skuGroupName || item.SkuGroupName || item.SKUGroupName || ""
    ).trim();
    const groupKey = orderItemId || `group-${groupIndex}`;
    const skuValues = item.skUs ?? item.SkUs ?? item.SKUs;
    if (!Array.isArray(skuValues)) {
      return [];
    }

    const skus = skuValues.flatMap((skuRaw, index) => {
      const skuRecord = asRecord(skuRaw);
      const sku = (
        typeof skuRaw === "string"
          ? skuRaw
          : String(skuRecord?.sku || skuRecord?.SKU || "")
      ).trim();
      if (!sku) {
        return [];
      }
      return [
        {
          id: reorderSkuSelectionId(groupKey, index, sku),
          sku,
          quantity: 1,
          orderItemId,
          skuGroupName,
          isParent: false,
          isSkuGroup: false,
        },
      ];
    });

    if (!skus.length) {
      return [];
    }

    return [
      {
        id: groupKey,
        skuGroupName,
        price: groupPrice(item.price ?? item.Price),
        orderItemId,
        skus,
      },
    ];
  });

export const dedupeReorderSkuItems = (items: IReorderSkuItem[]) => {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = item.id || item.sku.trim().toLowerCase();
    if (!key || seen.has(key)) {
      return false;
    }
    const nameKey = item.sku.trim().toLowerCase();
    if (nameKey && seen.has(`name:${nameKey}`)) {
      return false;
    }
    seen.add(key);
    if (nameKey) {
      seen.add(`name:${nameKey}`);
    }
    return true;
  });
};
